import React from 'react';
import {
	MessageInfo,
	NotificationContext,
	NotificationContextType,
} from 'context';
import {v4 as uuidv4} from 'uuid';
import {MessageBarType} from 'components/FluentUICompat';

export const NotificationProvider: React.FC<{
	children: React.ReactNode;
}> = ({children}) => {
	const [message, setMsg] =
		React.useState<NotificationContextType['message']>(undefined);
	const [messageBarType, setMessageBarType] = React.useState<
		MessageBarType | undefined
	>(undefined);
	const [onShowMoreClick, setOnShowMoreClick] = React.useState<
		(() => void) | undefined
	>(undefined);
	const [messageId, setMsgId] =
		React.useState<NotificationContextType['messageId']>(undefined);
	const lastMessageSignatureRef = React.useRef<string>('');

	const getMessageSignature = React.useCallback(
		(message: NotificationContextType['message']) => {
			if (message === undefined) {
				return '';
			}

			if (typeof message === 'string') {
				return `text:${message}`;
			}

			return `msg:${message.message}|timeout:${String(
				message.timeout,
			)}|type:${String(message.options?.messageBarType)}`;
		},
		[],
	);

	const setMessage = React.useCallback(
		(message: NotificationContextType['message']) => {
			const messageSignature = getMessageSignature(message);

			if (messageSignature === lastMessageSignatureRef.current) {
				return;
			}

			lastMessageSignatureRef.current = messageSignature;
			setMsg(message);
			setMsgId(uuidv4());
			setMessageBarType((message as MessageInfo)?.options?.messageBarType);
			setOnShowMoreClick(
				() => (message as MessageInfo)?.options?.onShowMoreClick,
			);
		},
		[getMessageSignature, setMsg, setMsgId],
	);

	const notificationContextValue = React.useMemo(
		() => ({
			message,
			messageId,
			messageBarType,
			onShowMoreClick,
			setMessage,
		}),
		[message, messageId, messageBarType, onShowMoreClick, setMessage],
	);

	return (
		<NotificationContext.Provider value={notificationContextValue}>
			{children}
		</NotificationContext.Provider>
	);
};
